/**
 * How far an account is below the benchmark, in dollars.
 *
 * Dollars, not points, because dollars are what the list is SORTED by. Showing
 * points beside a dollar ordering makes a correctly sorted table look broken:
 * 18.5, 29.6, 34.0, 33.6, 59.5 down the page, with no visible reason for the
 * order.
 *
 * Colour is used sparingly on purpose. Sorting worst-first means the top of
 * this screen is always the accounts furthest below target -- painting every
 * one of them red says nothing, because a signal that is always on is not a
 * signal. The number carries the magnitude; the points sit beside it as
 * context.
 */
const money = (n: number) =>
  n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });

export function BelowTargetCell({
  gapDollars,
  marginPct,
  benchmarkPct,
}: {
  gapDollars: number | null;
  marginPct: number | null;
  benchmarkPct: number | null;
}) {
  if (gapDollars === null || marginPct === null || benchmarkPct === null) {
    return <span className="text-muted-foreground">&mdash;</span>;
  }

  if (gapDollars <= 0) {
    // At or above target. Stated plainly rather than as a green badge -- this
    // column exists to size a shortfall, and "none" is the useful reading.
    return <span className="text-muted-foreground">On target</span>;
  }

  const points = benchmarkPct - marginPct;
  return (
    <span className="flex flex-col items-end leading-tight">
      <span className="tabular-nums font-medium text-foreground">{money(gapDollars)}</span>
      <span className="text-xs text-muted-foreground">{points.toFixed(1)} pts</span>
    </span>
  );
}
